import {
  Button,
  ButtonIcon,
  css,
  Inline,
  Plus1Icon,
  SelectInput,
  styled,
  TextInput,
  TrashIcon,
} from 'canoe';
import React from 'react';

import { defaultUrl, PlacementUrl, URLOptions, urlSelectOptions } from '@candu-ai/placement/types';

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const SelectContainer = styled.div`
  ${({ theme }) => css`
    align-self: stretch;
    align-items: center;
    display: flex;
    flex: 1;
    margin-bottom: ${theme.spaces.xs};

    > div {
      flex: 1;
    }

    &:hover
      ${OptionsSelect},
      &:focus
      ${OptionsSelect},
      &:hover
      ${StyledInput},
      &:focus
      ${StyledInput} {
      border-color: ${theme.colors.AquaTeal};
      box-shadow: 0 0 0 1px ${theme.colors.AquaTeal};
    }
  `}
`;

const OptionsSelect = styled(SelectInput)`
  ${({ theme }) => css`
    background-color: ${theme.colors.GlitterGrayL30};
    border-radius: ${theme.spaces.xxs} 0 0 ${theme.spaces.xxs};
    width: 226px;
    max-width: 40%;
  `}
`;

const StyledInput = styled(TextInput)`
  ${({ theme }) => css`
    border-radius: 0 ${theme.spaces.xxs} ${theme.spaces.xxs} 0;
  `}
`;

interface Props {
  urls: PlacementUrl[];
  updateUrls: (values: PlacementUrl[], resetTouched?: boolean) => void;
  scale?: 'default' | 'small';
  disabled?: boolean;
  urlErrors?: string;
}

export const MultiplePagesInput = ({ disabled, urls, updateUrls, scale, urlErrors }: Props) => {
  const onChangeOperationType = (index: number) => (item: { type: URLOptions; not: boolean }) => {
    const newUrls = urls.map((url, idx) =>
      idx !== index
        ? url
        : { ...url, not: item.not, operation: { ...url.operation, type: item.type } },
    );
    updateUrls(newUrls);
  };

  const setOperand = (index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const newUrls = urls.map((url, idx) =>
      idx !== index
        ? url
        : {
            ...url,
            operation: {
              ...url.operation,
              operand: e.target.value,
              // if URL contains a '*' then auto-switch to wildcard
              type: e.target.value.includes('*') ? URLOptions.REGEX : url.operation.type,
            },
          },
    );

    updateUrls(newUrls);
  };

  const validateOperands = () => {
    // if an operand field is blurred, set field as touched and validate
    updateUrls(urls, false);
  };

  return (
    <>
      {(urls || []).map(({ operation, not }, index) => (
        <Container key={index.toString()}>
          <SelectContainer>
            <OptionsSelect
              disabled={disabled}
              scale={scale}
              items={urlSelectOptions}
              selectedItem={urlSelectOptions.find(
                (item) => item.type === operation.type && (item.not === not || !not),
              )}
              onSelect={onChangeOperationType(index)}
              clearable={false}
            />
            <StyledInput
              disabled={disabled}
              scale={scale}
              value={operation.operand}
              onChange={setOperand(index)}
              placeholder="https://yourapp.io/dashboard"
              state={urlErrors && !operation.operand ? 'danger' : 'none'}
              onBlur={validateOperands}
            />
            {(urls?.length || 0) > 1 && (
              <ButtonIcon
                size={scale === 'small' ? 'small' : 'normal'}
                ml="xxs"
                color="contrast"
                icon={TrashIcon}
                disabled={disabled}
                onClick={() => updateUrls(urls.filter((item, idx) => idx !== index))}
              />
            )}
          </SelectContainer>
          {urls?.length === index + 1 &&
            (scale === 'small' ? (
              <Inline justify="center">
                <Button
                  size={scale}
                  icon={Plus1Icon}
                  variant="secondary"
                  appearance="text"
                  disabled={!operation.operand || disabled}
                  onClick={() => updateUrls([...urls, defaultUrl], true)}
                  fontWeight="normal"
                >
                  Add another URL rule
                </Button>
              </Inline>
            ) : (
              <Inline space="none" justify="flex-end" mt="xs">
                <Button
                  variant="secondary"
                  appearance="outline"
                  disabled={!operation.operand || disabled}
                  onClick={() => updateUrls([...urls, defaultUrl], true)}
                >
                  Add another rule
                </Button>
              </Inline>
            ))}
        </Container>
      ))}
    </>
  );
};
